import { Theme } from '@material-ui/core/styles';
import { CreateCSSProperties, createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ReactComponent as MonetaIcon } from '@/assets/icons/moneta.svg';
import { ReactComponent as HelpIcon } from '@/assets/icons/question.svg';
import { BodyDetails, BodyMain, MoneyAmountValue, ReadonlyItem, Space, Tooltip } from '@/components';
import { MainLayout } from '@/layout';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import { UIState } from '@/model/global/types';
import { makeSelectLimits } from '@/model/invoice/selectors';
import { InvoiceUIState } from '@/model/invoice/types';
import { InvoiceDetailsView, InvoiceLimitsView } from '@/pages/invoice/components';

const useStyles = makeStyles((theme: Theme) => {
  const footer: CreateCSSProperties = {
    borderTop: '1px solid rgba(195, 195, 217, 0.6)',
    backgroundColor: theme.colors.lightBackground,
  };

  return createStyles({
    footer: {
      ...footer,
      paddingTop: theme.spacing(2),
    },
    footerMobile: {
      ...footer,
    },
  });
});

const selectLimits = makeSelectLimits();
const selectIsMobile = makeSelectIsMobileView();

export const DetailsPage: React.FC = () => {
  const classes = useStyles();
  const limits = useSelector(selectLimits);
  const isMobile = useSelector(selectIsMobile);
  const history = useHistory();

  return (
    <MainLayout
      height={550}
      minHeight={350}
      buttons={{
        logout: <FormattedMessage id="invoice.logout.button.title" />,
        main: {
          submit: false,
          type: 'secondary',
          title: <FormattedMessage id="invoice.pages.details.banks.link" />,
          onAct: (): void => history.push(`/${UIState.INVOICE}/${InvoiceUIState.BANKS}`),
        },
      }}
      footer={{
        className: isMobile ? classes.footerMobile : classes.footer,
        component: limits ? <InvoiceLimitsView value={limits} /> : undefined,
      }}
      header={{
        title: <FormattedMessage id="invoice.pages.details.title" />,
      }}
    >
      <Space direction="vertical" size={2}>
        <BodyMain>
          <FormattedMessage
            id="invoice.pages.details.description"
            values={{
              help: (
                <Tooltip placement="top" title={<FormattedMessage id="invoice.pages.details.help" />}>
                  <HelpIcon style={{ verticalAlign: 'bottom' }} />
                </Tooltip>
              ),
            }}
          />
        </BodyMain>
        <InvoiceDetailsView />
        <ReadonlyItem label={<FormattedMessage id="invoice.pages.details.fields.bank.label" />}>
          <Space>
            <MonetaIcon />
            <BodyDetails>
              <FormattedMessage id="invoice.pages.details.fields.bank.value" />
            </BodyDetails>
          </Space>
        </ReadonlyItem>
        <ReadonlyItem label={<FormattedMessage id="invoice.pages.details.fields.sum.label" />} noTruncate>
          <FormattedMessage id="invoice.pages.details.fields.sum.value.prefix" />
          {limits?.oneTime && (
            <FormattedMessage
              id="invoice.pages.details.fields.sum.value.oneTime"
              values={{
                value: (
                  <MoneyAmountValue style={{ display: 'inline-flex' }} value={limits.oneTime.amount} withCurrency />
                ),
              }}
            />
          )}
          {limits?.monthly && (
            <FormattedMessage
              id="invoice.pages.details.fields.sum.value.monthly"
              values={{
                value: (
                  <MoneyAmountValue style={{ display: 'inline-flex' }} value={limits.monthly.amount} withCurrency />
                ),
              }}
            />
          )}
        </ReadonlyItem>
      </Space>
    </MainLayout>
  );
};

export default DetailsPage;
